/**
 * 右栏分镜检查器（v2 - tab 分组 + 自动保存）
 *
 * 设计：
 *  - 顶部：分镜标题 + 自动保存状态指示
 *  - Tab：内容 / 关键帧 / 视频 三段（对应工作流三步）
 *  - 底部 sticky 状态条：当前激活状态 + 一键激活/重激活/取消（任意 tab 都能用）
 *  - 编辑后 800ms debounce 自动保存，不再有"保存"按钮
 *
 * 子文件：
 *  - tabs/ShotContentTab.tsx
 *  - tabs/ShotKeyframeTab.tsx
 *  - tabs/ShotTrinityCardsTab.tsx
 *  - tabs/ShotVideoTab.tsx
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Loader2,
  Check,
  AlertCircle,
  Type,
  ImageIcon,
  Film,
  Layers,
  Image as ImageIconLg,
  HelpCircle,
  Sparkles,
} from 'lucide-react';
import toast from '../../../../components/Toast';
import {
  directorApi,
  DirectorApiError,
  type CharacterLayoutItem,
  type DirectorCharacter,
  type DirectorProp,
  type DirectorScene,
  type DirectorShotEnriched,
  type DirectorShotMode,
} from '../../services/directorApi';
import { useShotPolling } from '../../hooks/useShotPolling';
import { extractMentions, type MentionCandidate } from './MentionTextarea';
import { ShotContentTab } from './tabs/ShotContentTab';
import { ShotKeyframeTab } from './tabs/ShotKeyframeTab';
import { ShotFrameSequenceTab } from './tabs/ShotFrameSequenceTab';
import { ShotTrinityCardsTab } from './tabs/ShotTrinityCardsTab';
import { ShotVideoTab } from './tabs/ShotVideoTab';
import { KeyframeGenerateConfirmDialog } from './KeyframeGenerateConfirmDialog';
import { FrameGenerateConfirmDialog } from './FrameGenerateConfirmDialog';
import type { DurationSpec } from '../../../../baselines/video/services/videoApi';

type TabKey = 'content' | 'keyframe' | 'video';
type ActivationStatus =
  | 'none'
  | 'queued'
  | 'pending'
  | 'submitting'
  | 'processing'
  | 'completed'
  | 'failed'
  | 'timeout';

interface Props {
  shot: DirectorShotEnriched;
  projectAspectRatio: '16:9' | '9:16' | '1:1' | string;
  projectCharacters: DirectorCharacter[];
  projectScenes: DirectorScene[];
  projectProps: DirectorProp[];
  videoActive: boolean;
  activationStatus?: ActivationStatus;
  /** 当前激活视频的 URL（completed 时用于预览） */
  videoUrl?: string | null;
  /** 当前用户余额（用于关键帧 / 视频生成的二次确认） */
  credits: number;
  /** 项目所选视频模型在 img2video 模式下的时长能力声明（按 adapter） */
  videoDurationSpec?: DurationSpec | null;
  /** 项目所选视频模型的默认时长（用作未设置时的占位） */
  videoDurationDefault?: number | null;
  onShotUpdated: () => void;
  onRequestActivate: () => void;
  onRequestReactivate: () => void;
  onRequestDeactivate: () => void;
  onRequestImportKeyframe?: () => void;
}

const AUTOSAVE_DELAY_MS = 800;

export function ShotInspector({
  shot,
  projectAspectRatio,
  projectCharacters,
  projectScenes,
  projectProps,
  videoActive,
  activationStatus = 'none',
  videoUrl = null,
  credits,
  videoDurationSpec = null,
  videoDurationDefault = null,
  onShotUpdated,
  onRequestActivate,
  onRequestReactivate,
  onRequestDeactivate,
  onRequestImportKeyframe,
}: Props) {
  // ===== 表单字段（受控，编辑触发自动保存） =====
  const [prompt, setPrompt] = useState(shot.prompt);
  const [duration, setDuration] = useState<number | null>(shot.duration);
  // 注：injectedSceneId 不再是独立 state——场景统一通过 prompt 内 @ 引用决定。
  // 解析 mentionedSceneId 来自 useMemo(extractMentions(...))，autosave 时同步写回 DB。
  const [characterLayout, setCharacterLayout] = useState<CharacterLayoutItem[]>(() => {
    if (!shot.characterLayout) return [];
    try {
      const parsed = JSON.parse(shot.characterLayout);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  });
  const [cameraMovement, setCameraMovement] = useState<string | null>(shot.cameraMovement);
  const [motionIntensity, setMotionIntensity] = useState<string | null>(shot.motionIntensity);

  // ===== UI 状态 =====
  const [activeTab, setActiveTab] = useState<TabKey>('content');
  const [expandKeyframes, setExpandKeyframes] = useState(false);
  const [autosave, setAutosave] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const isFrameSequenceMode = shot.mode === 'frame_sequence';
  const isTrinityMode = shot.mode === 'trinity_cards';
  const hasVisualReference = isFrameSequenceMode
    ? (shot.frameCount ?? 0) > 0
    : isTrinityMode
      ? (shot.trinityCardCount ?? 0) >= 3
      : !!shot.lockedKeyframeImageJobId;

  // 关键帧生成确认弹窗（弹窗内自管理模型 / 尺寸 / 画质 + 实时预估）
  const [keyframeConfirmOpen, setKeyframeConfirmOpen] = useState(false);
  const [keyframeSubmitting, setKeyframeSubmitting] = useState(false);

  // 帧候选生成弹窗（image_sequence 模式专用）
  const [frameConfirmOpen, setFrameConfirmOpen] = useState(false);
  const [frameSubmitting, setFrameSubmitting] = useState(false);

  const mentionCandidates: MentionCandidate[] = useMemo(
    () => [
      ...projectCharacters.map((c) => ({
        id: c.id,
        name: c.name,
        kind: 'character' as const,
      })),
      ...projectProps.map((p) => ({
        id: p.id,
        name: p.name,
        kind: 'prop' as const,
      })),
      ...projectScenes.map((s) => ({
        id: s.id,
        name: s.name,
        kind: 'scene' as const,
      })),
    ],
    [projectCharacters, projectProps, projectScenes]
  );

  const {
    characterIds: injectedCharIds,
    propIds: injectedPropIds,
    sceneId: mentionedSceneId,
    sceneIdsExtra: mentionedSceneIdsExtra,
  } = useMemo(
    () =>
      extractMentions(
        prompt,
        projectCharacters.map((c) => ({ id: c.id, name: c.name })),
        projectProps.map((p) => ({ id: p.id, name: p.name })),
        projectScenes.map((s) => ({ id: s.id, name: s.name }))
      ),
    [prompt, projectCharacters, projectProps, projectScenes]
  );

  const mentionedCharacters = useMemo(
    () =>
      injectedCharIds
        .map((id) => projectCharacters.find((c) => c.id === id))
        .filter((c): c is DirectorCharacter => !!c),
    [injectedCharIds, projectCharacters]
  );

  const mentionedProps = useMemo(
    () =>
      injectedPropIds
        .map((id) => projectProps.find((p) => p.id === id))
        .filter((p): p is DirectorProp => !!p),
    [injectedPropIds, projectProps]
  );

  const mentionedScene = useMemo(
    () =>
      mentionedSceneId
        ? projectScenes.find((scene) => scene.id === mentionedSceneId) ?? null
        : null,
    [mentionedSceneId, projectScenes]
  );

  // ===== 切换 shot 时重置所有状态 =====
  // 用 ref 标记"是否处于 shot 切换的初始化窗口"，避免初始化期间的 setState 触发 autosave
  const isLoadingShotRef = useRef(true);
  useEffect(() => {
    isLoadingShotRef.current = true;
    setPrompt(shot.prompt);
    setDuration(shot.duration);
    // injectedSceneId 已不再用 state；shot.injectedSceneId 会通过 prompt 重置自动反映在 mention 解析中
    setCameraMovement(shot.cameraMovement);
    setMotionIntensity(shot.motionIntensity);
    try {
      const parsed = shot.characterLayout ? JSON.parse(shot.characterLayout) : [];
      setCharacterLayout(Array.isArray(parsed) ? parsed : []);
    } catch {
      setCharacterLayout([]);
    }
    // 默认 tab：需要先准备参考素材的模式优先打开素材 tab，单图模式优先内容 tab
    setActiveTab(
      shot.mode === 'frame_sequence' || shot.mode === 'trinity_cards' ? 'keyframe' : 'content'
    );
    setExpandKeyframes(false);
    setAutosave('idle');
    // 下一帧再放开，让本次 setState 不触发 autosave
    const t = setTimeout(() => {
      isLoadingShotRef.current = false;
    }, 0);
    return () => clearTimeout(t);
  }, [shot.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // ===== Debounce 自动保存 =====
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (isLoadingShotRef.current) return;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    setAutosave('saving');
    saveTimerRef.current = setTimeout(async () => {
      try {
        await directorApi.updateShot(shot.id, {
          prompt,
          duration,
          injectedCharacterIds: injectedCharIds.length > 0 ? injectedCharIds : null,
          injectedPropIds: injectedPropIds.length > 0 ? injectedPropIds : null,
          injectedSceneId: mentionedSceneId,
          cameraMovement,
          motionIntensity,
          characterLayout: characterLayout.length > 0 ? characterLayout : null,
        });
        setAutosave('saved');
        onShotUpdated();
      } catch (err) {
        setAutosave('error');
        toast.error(err instanceof DirectorApiError ? err.message : '自动保存失败');
      }
    }, AUTOSAVE_DELAY_MS);
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    prompt,
    duration,
    mentionedSceneId,
    cameraMovement,
    motionIntensity,
    JSON.stringify(characterLayout),
    JSON.stringify(injectedCharIds),
    JSON.stringify(injectedPropIds),
  ]);

  // 「已保存」状态展示 1.5s 后回到 idle
  useEffect(() => {
    if (autosave !== 'saved') return;
    const t = setTimeout(() => setAutosave('idle'), 1500);
    return () => clearTimeout(t);
  }, [autosave]);

  // ===== Keyframe 候选轮询 =====
  const polling = useShotPolling(shot.id, expandKeyframes ? { limit: 100 } : {});

  // ===== Keyframe 操作 =====
  const handleGenerateKeyframe = () => {
    if (!prompt.trim()) {
      toast.error('请先填写提示词');
      return;
    }
    setKeyframeConfirmOpen(true);
  };

  const handleConfirmGenerateKeyframe = async (input: {
    modelId: string;
    size: string;
    quality: 'low' | 'medium' | 'high';
  }) => {
    setKeyframeSubmitting(true);
    try {
      const result = await directorApi.generateKeyframe(shot.id, {
        prompt,
        injectedCharacterIds: injectedCharIds.length > 0 ? injectedCharIds : undefined,
        injectedPropIds: injectedPropIds.length > 0 ? injectedPropIds : undefined,
        injectedSceneId: mentionedSceneId ?? undefined,
        modelId: input.modelId || undefined,
        size: input.size,
        quality: input.quality,
      });
      toast.success(`候选生成中（消耗 ${result.creditsUsed} 积分）`);
      setKeyframeConfirmOpen(false);
    } catch (err) {
      toast.error(err instanceof DirectorApiError ? err.message : '生成失败');
    } finally {
      setKeyframeSubmitting(false);
    }
  };

  // ===== 帧候选（image_sequence）=====
  const handleConfirmGenerateFrame = async (input: {
    prompt: string;
    injectedCharacterIds?: string[];
    injectedPropIds?: string[];
    injectedSceneId?: string | null;
    modelId: string;
    size: string;
    quality: 'low' | 'medium' | 'high';
  }) => {
    if (!input.prompt.trim()) {
      toast.error('请填写提示词');
      return;
    }
    setFrameSubmitting(true);
    try {
      const result = await directorApi.generateFrame(shot.id, {
        prompt: input.prompt,
        injectedCharacterIds: input.injectedCharacterIds?.length
          ? input.injectedCharacterIds
          : undefined,
        injectedPropIds: input.injectedPropIds?.length
          ? input.injectedPropIds
          : undefined,
        injectedSceneId: input.injectedSceneId ?? undefined,
        modelId: input.modelId || undefined,
        size: input.size,
        quality: input.quality,
      });
      toast.success(`候选生成中（消耗 ${result.creditsUsed} 积分）`);
      setFrameConfirmOpen(false);
      // 帧序列 tab 内部会自己轮询 / reload
    } catch (err) {
      toast.error(err instanceof DirectorApiError ? err.message : '生成失败');
    } finally {
      setFrameSubmitting(false);
    }
  };
  const handleLockKeyframe = async (imageJobId: string) => {
    try {
      await directorApi.lockKeyframe(shot.id, imageJobId);
      toast.success('已锁定');
      onShotUpdated();
    } catch (err) {
      toast.error(err instanceof DirectorApiError ? err.message : '锁定失败');
    }
  };
  const handleUnlockKeyframe = async () => {
    try {
      await directorApi.unlockKeyframe(shot.id);
      toast.success('已解锁');
      onShotUpdated();
    } catch (err) {
      toast.error(err instanceof DirectorApiError ? err.message : '解锁失败');
    }
  };
  const handleDeleteKeyframe = async (imageJobId: string) => {
    try {
      await directorApi.deleteKeyframe(imageJobId);
      toast.success('已删除');
      onShotUpdated();
    } catch (err) {
      toast.error(err instanceof DirectorApiError ? err.message : '删除失败');
    }
  };

  // ===== 派生 =====
  const isCompleted = activationStatus === 'completed';
  const isInProgress =
    activationStatus === 'queued' ||
    activationStatus === 'pending' ||
    activationStatus === 'submitting' ||
    activationStatus === 'processing';
  const isFailed = activationStatus === 'failed' || activationStatus === 'timeout';
  const hasLockedKeyframe = !!shot.lockedKeyframeImageJobId;

  // 各 tab 的小角标计数（视觉提示用户哪里有内容）
  const contentBadge = useMemo(() => {
    let count = 0;
    if (prompt.trim()) count++;
    if (shot.mode === 'frame_sequence') {
      // 多图模式：只统计提示词 + 镜头运动（角色/物品/场景/构图不参与）
      if (cameraMovement || motionIntensity) count++;
      return count;
    }
    if (mentionedCharacters.length > 0) count++;
    if (mentionedProps.length > 0) count++;
    if (mentionedSceneId) count++;
    if (characterLayout.length > 0 || cameraMovement || motionIntensity) count++;
    return count;
  }, [
    prompt,
    shot.mode,
    mentionedCharacters.length,
    mentionedProps.length,
    mentionedSceneId,
    characterLayout.length,
    cameraMovement,
    motionIntensity,
  ]);

  // ===== 模式切换（keyframe ↔ frame_sequence ↔ trinity_cards） =====
  const [modeSwitching, setModeSwitching] = useState(false);
  const [showModeHelp, setShowModeHelp] = useState(false);
  const [modeSwitch, setModeSwitch] = useState({
    keyframeEnabled: true,
    frameSequenceEnabled: true,
    trinityCardsEnabled: true,
  });

  useEffect(() => {
    directorApi
      .getPublicSettings()
      .then((res) => {
        setModeSwitch({
          keyframeEnabled: res.keyframeEnabled,
          frameSequenceEnabled: res.frameSequenceEnabled,
          trinityCardsEnabled: res.trinityCardsEnabled,
        });
      })
      .catch(() => undefined);
  }, []);

  const handleSwitchMode = async (target: DirectorShotMode) => {
    if (target === shot.mode) return;
    if (videoActive) {
      toast.error('视频已激活，请先取消或重激活后再切换模式');
      return;
    }
    // 切换是完全无损的：候选图自动迁移 usageRole；锁定指针 / 帧序列关联在对端模式下
    // 沉默不被读取，保留即可。来回切都能恢复。所以不再需要二次确认弹窗。
    await doSwitchMode(target);
  };

  const doSwitchMode = async (target: DirectorShotMode) => {
    setModeSwitching(true);
    try {
      await directorApi.changeShotMode(shot.id, target);
      toast.success(
        target === 'frame_sequence'
          ? '已切换到「多图 → 视频」'
          : target === 'trinity_cards'
            ? '已切换到「三合一参考」'
            : '已切换到「单图 → 视频」'
      );
      setActiveTab('keyframe');
      onShotUpdated();
    } catch (err) {
      toast.error(err instanceof DirectorApiError ? err.message : '切换失败');
    } finally {
      setModeSwitching(false);
    }
  };

  // ===== 渲染 =====
  return (
    <>
    <div className="flex h-full flex-col">
      {/* 顶部：标题 + 自动保存指示 */}
      <div className="flex items-center justify-between gap-2 border-b border-slate-100 pb-3">
        <h3 className="flex min-w-0 items-center gap-2 text-sm font-semibold text-slate-900">
          <span
            className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[11px] font-bold text-white shadow-sm ${
              shot.mode === 'frame_sequence'
                ? 'bg-gradient-to-br from-amber-500 to-orange-400'
                : shot.mode === 'trinity_cards'
                  ? 'bg-gradient-to-br from-violet-500 to-fuchsia-500'
                  : 'bg-gradient-to-br from-sky-500 to-cyan-400'
            }`}
          >
            #{shot.shotOrderInProject + 1}
          </span>
          <span className="truncate">
            {shot.title || `分镜 #${shot.shotOrderInProject + 1}`}
          </span>
        </h3>
        <AutosaveIndicator state={autosave} />
      </div>

      {/* 分镜模式切换 segmented control */}
      <div className="mt-3 flex items-center gap-2">
        <span className="inline-flex items-center gap-1 text-[10px] font-medium uppercase tracking-[0.18em] text-slate-400">
          模式
          <button
            type="button"
            onClick={() => setShowModeHelp(true)}
            className="rounded-full p-0.5 text-slate-300 transition hover:bg-slate-100 hover:text-slate-600"
            title="了解三种模式的区别"
            aria-label="了解模式区别"
          >
            <HelpCircle className="h-3 w-3" />
          </button>
        </span>
        <div className="flex flex-1 items-center rounded-2xl bg-slate-100 p-0.5">
          {modeSwitch.keyframeEnabled && (
            <ModeOption
              active={shot.mode === 'keyframe'}
              disabled={modeSwitching || videoActive}
              accent="sky"
              icon={<ImageIconLg className="h-3.5 w-3.5" />}
              label="单图"
              hint="1 张图生成视频"
              onClick={() => handleSwitchMode('keyframe')}
            />
          )}
          {modeSwitch.frameSequenceEnabled && (
            <ModeOption
              active={shot.mode === 'frame_sequence'}
              disabled={modeSwitching || videoActive}
              accent="amber"
              icon={<Layers className="h-3.5 w-3.5" />}
              label="多图"
              hint="多张图过渡成视频"
              onClick={() => handleSwitchMode('frame_sequence')}
            />
          )}
          {modeSwitch.trinityCardsEnabled && (
            <ModeOption
              active={shot.mode === 'trinity_cards'}
              disabled={modeSwitching || videoActive}
              accent="violet"
              icon={<Sparkles className="h-3.5 w-3.5" />}
              label="三合一"
              hint="角色卡 / 故事卡 / 视频卡共同参考"
              onClick={() => handleSwitchMode('trinity_cards')}
            />
          )}
        </div>
      </div>

      {/* Tab 切换 */}
      <div className="mt-3 flex items-center gap-1 rounded-2xl bg-slate-100 p-1">
        {(() => {
          const contentBtn = (
            <TabButton
              key="content"
              active={activeTab === 'content'}
              onClick={() => setActiveTab('content')}
              icon={<Type className="h-3.5 w-3.5" />}
              label={
                shot.mode === 'frame_sequence'
                  ? '视频指导'
                  : shot.mode === 'trinity_cards'
                    ? '卡片+视频指导'
                    : '图片+视频指导'
              }
              badge={contentBadge > 0 ? contentBadge : undefined}
            />
          );
          const keyframeBtn = (
            <TabButton
              key="keyframe"
              active={activeTab === 'keyframe'}
              onClick={() => setActiveTab('keyframe')}
              icon={<ImageIcon className="h-3.5 w-3.5" />}
              label={
                shot.mode === 'frame_sequence'
                  ? '多图 → 视频'
                  : shot.mode === 'trinity_cards'
                    ? '三合一参考'
                    : '单图 → 视频'
              }
              badge={
                shot.mode === 'trinity_cards'
                  ? shot.trinityCardCount ?? 0
                  : polling.keyframes.length > 0
                    ? polling.keyframes.length
                    : undefined
              }
              highlight={
                shot.mode === 'trinity_cards'
                  ? (shot.trinityCardCount ?? 0) > 0
                    ? 'violet'
                    : undefined
                  : hasLockedKeyframe
                    ? 'sky'
                    : undefined
              }
            />
          );
          const videoBtn = (
            <TabButton
              key="video"
              active={activeTab === 'video'}
              onClick={() => setActiveTab('video')}
              icon={<Film className="h-3.5 w-3.5" />}
              label="视频"
              highlight={isCompleted ? 'emerald' : isInProgress ? 'amber' : isFailed ? 'rose' : undefined}
            />
          );
          // 需要先准备视觉参考的模式：素材 tab 置首，视频指导置中，视频置尾
          return shot.mode === 'frame_sequence' || shot.mode === 'trinity_cards'
            ? [keyframeBtn, contentBtn, videoBtn]
            : [contentBtn, keyframeBtn, videoBtn];
        })()}
      </div>

      {/* Tab 内容（可滚动区） */}
      <div className="mt-4 flex-1 overflow-y-auto pb-4 pr-1">
        {activeTab === 'content' && (
          <ShotContentTab
            shotMode={shot.mode}
            projectAspectRatio={projectAspectRatio}
            projectCharacters={projectCharacters}
            projectScenes={projectScenes}
            projectProps={projectProps}
            prompt={prompt}
            mentionedSceneId={mentionedSceneId}
            mentionedSceneIdsExtra={mentionedSceneIdsExtra}
            cameraMovement={cameraMovement}
            motionIntensity={motionIntensity}
            characterLayout={characterLayout}
            mentionCandidates={mentionCandidates}
            mentionedCharacters={mentionedCharacters}
            mentionedProps={mentionedProps}
            videoActive={videoActive}
            setPrompt={setPrompt}
            setCharacterLayout={setCharacterLayout}
            setCameraMovement={setCameraMovement}
            setMotionIntensity={setMotionIntensity}
          />
        )}
        {activeTab === 'keyframe' &&
          (shot.mode === 'frame_sequence' ? (
            <ShotFrameSequenceTab
              shotId={shot.id}
              videoActive={videoActive}
              currentCredits={credits}
              shotPrompt={prompt}
              projectCharacters={projectCharacters}
              projectProps={projectProps}
              projectScenes={projectScenes}
              projectAspectRatio={projectAspectRatio}
              onRequestGenerate={() => setFrameConfirmOpen(true)}
              onSequenceChanged={onShotUpdated}
            />
          ) : shot.mode === 'trinity_cards' ? (
            <ShotTrinityCardsTab
              shotId={shot.id}
              shotPrompt={prompt}
              injectedCharacterIds={injectedCharIds}
              injectedPropIds={injectedPropIds}
              injectedSceneId={mentionedSceneId}
              injectedCharacterNames={mentionedCharacters.map((item) => item.name)}
              injectedPropNames={mentionedProps.map((item) => item.name)}
              injectedSceneName={mentionedScene?.name ?? null}
              currentCredits={credits}
              videoActive={videoActive}
              initialCards={shot.trinityCards ?? []}
              onShotUpdated={onShotUpdated}
            />
          ) : (
            <ShotKeyframeTab
              candidates={polling.keyframes}
              lockedJobId={shot.lockedKeyframeImageJobId}
              videoActive={videoActive}
              hasMore={polling.hasMore}
              expandKeyframes={expandKeyframes}
              setExpandKeyframes={setExpandKeyframes}
              onGenerateNew={handleGenerateKeyframe}
              onLock={handleLockKeyframe}
              onUnlock={handleUnlockKeyframe}
              onDelete={handleDeleteKeyframe}
              onRequestImport={onRequestImportKeyframe}
            />
          ))}
        {activeTab === 'video' && (
          <ShotVideoTab
            hasVisualReference={hasVisualReference}
            shotMode={shot.mode}
            shotId={shot.id}
            activationStatus={activationStatus}
            videoUrl={videoUrl}
            duration={duration}
            videoActive={videoActive}
            setDuration={setDuration}
            videoDurationSpec={videoDurationSpec ?? null}
            videoDurationDefault={videoDurationDefault ?? null}
            candidateVideos={shot.candidateVideos ?? []}
            onShotUpdated={onShotUpdated}
            onRequestActivate={onRequestActivate}
            onRequestReactivate={onRequestReactivate}
            onRequestDeactivate={onRequestDeactivate}
            onGoToVisualTab={() => setActiveTab('keyframe')}
          />
        )}
      </div>
    </div>

    {/* 关键帧生成确认弹窗 */}
    {keyframeConfirmOpen && (
      <KeyframeGenerateConfirmDialog
        open
        shotId={shot.id}
        projectAspectRatio={projectAspectRatio}
        injectedCharacterIds={injectedCharIds}
        injectedPropIds={injectedPropIds}
        injectedSceneId={mentionedSceneId}
        refCount={
          injectedCharIds.length +
          injectedPropIds.length +
          (mentionedSceneId &&
          projectScenes.find((s) => s.id === mentionedSceneId)?.settingImageJobId
            ? 1
            : 0)
        }
        currentCredits={credits}
        submitting={keyframeSubmitting}
        onCancel={() => setKeyframeConfirmOpen(false)}
        onConfirm={handleConfirmGenerateKeyframe}
      />
    )}

    {/* 帧候选生成弹窗（image_sequence 专用） */}
    {frameConfirmOpen && (
      <FrameGenerateConfirmDialog
        open
        shotId={shot.id}
        projectAspectRatio={projectAspectRatio}
        shotPrompt={prompt}
        currentCredits={credits}
        projectCharacters={projectCharacters}
        projectProps={projectProps}
        projectScenes={projectScenes}
        submitting={frameSubmitting}
        onCancel={() => setFrameConfirmOpen(false)}
        onConfirm={handleConfirmGenerateFrame}
      />
    )}

    {/* 模式说明帮助弹窗 */}
    {showModeHelp && (
      <ModeHelpDialog open onClose={() => setShowModeHelp(false)} />
    )}
    </>
  );
}

/* -------------------- 子组件 -------------------- */

function ModeHelpDialog({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  if (!open) return null;
  if (typeof document === 'undefined') return null;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 p-4 backdrop-blur-sm">
      <button
        type="button"
        aria-label="关闭"
        className="absolute inset-0 cursor-default"
        onClick={onClose}
      />
      <div
        className="relative flex max-h-[88vh] w-full max-w-xl flex-col overflow-hidden rounded-[28px] bg-white shadow-2xl shadow-slate-950/30"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bg-gradient-to-br from-slate-950 via-slate-900 to-slate-800 p-5 text-white">
          <div className="mb-1 inline-flex items-center gap-1.5 rounded-full bg-white/10 px-2.5 py-0.5 text-[11px] font-medium text-slate-200">
            <HelpCircle className="h-3 w-3" />
            分镜模式说明
          </div>
          <h2 className="text-lg font-semibold leading-tight">三种分镜模式怎么选</h2>
          <p className="mt-1 text-xs leading-5 text-slate-300">
            三种模式最终都会产出 1 段视频，差别在于你准备哪种参考素材，以及视频模型如何使用这些参考图。
          </p>
        </div>

        <div className="flex-1 space-y-5 overflow-y-auto bg-slate-50 p-5 text-sm leading-6 text-slate-700">
          {/* 单图 → 视频 */}
          <section className="rounded-2xl bg-white p-4 ring-1 ring-sky-100">
            <div className="mb-2 flex items-center gap-2">
              <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-sky-500 to-cyan-400 text-white shadow-sm">
                <ImageIconLg className="h-4 w-4" />
              </span>
              <h3 className="text-sm font-semibold text-slate-900">
                单图 → 视频
                <span className="ml-2 rounded-md bg-sky-50 px-1.5 py-0.5 text-[10px] font-normal text-sky-700">
                  内部代号 keyframe
                </span>
              </h3>
            </div>
            <p className="text-[13px] leading-6">
              <b>1 张图 → 1 段视频。</b>用户准备 1 张「锁定关键帧」，视频模型拿到这 1 张图作为首帧，
              剩下的动作和画面由模型自己脑补。
            </p>
            <ul className="mt-2 space-y-1 text-[12px] leading-5 text-slate-500">
              <li>
                <b className="text-slate-700">适合：</b>单一动作 / 短时长 / 主体相对静态的镜头（如「人物坐在椅子上喝咖啡」）
              </li>
              <li>
                <b className="text-emerald-700">优势：</b>操作简单，单张图就能激活；积分成本低
              </li>
              <li>
                <b className="text-rose-700">劣势：</b>复杂动作 / 多个画面变化时，模型可能脑补走样；长时长容易漂移
              </li>
            </ul>
          </section>

          {/* 多图 → 视频 */}
          <section className="rounded-2xl bg-white p-4 ring-1 ring-amber-100">
            <div className="mb-2 flex items-center gap-2">
              <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-amber-500 to-orange-400 text-white shadow-sm">
                <Layers className="h-4 w-4" />
              </span>
              <h3 className="text-sm font-semibold text-slate-900">
                多图 → 视频
                <span className="ml-2 rounded-md bg-amber-50 px-1.5 py-0.5 text-[10px] font-normal text-amber-700">
                  内部代号 frame_sequence
                </span>
              </h3>
            </div>
            <p className="text-[13px] leading-6">
              <b>N 张图 → 1 段视频。</b>用户准备 N 张帧（按顺序），视频模型必须沿着这 N 张图的轨迹去过渡，
              更精准地控制画面走向。
            </p>
            <ul className="mt-2 space-y-1 text-[12px] leading-5 text-slate-500">
              <li>
                <b className="text-slate-700">适合：</b>复杂动作 / 关键节点要求精确的镜头（如「人物从坐着 → 站起来 → 走到窗边 → 推开窗户」）
              </li>
              <li>
                <b className="text-emerald-700">优势：</b>对画面走向控制更精准；主体一致性更稳；复杂动作不会失控
              </li>
              <li>
                <b className="text-rose-700">劣势：</b>要准备多张图（生成 + 排序）；单镜积分成本更高；要求视频模型支持多参考图
              </li>
            </ul>
          </section>

          {/* 三合一参考 */}
          <section className="rounded-2xl bg-white p-4 ring-1 ring-violet-100">
            <div className="mb-2 flex items-center gap-2">
              <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-500 text-white shadow-sm">
                <Sparkles className="h-4 w-4" />
              </span>
              <h3 className="text-sm font-semibold text-slate-900">
                三合一参考
                <span className="ml-2 rounded-md bg-violet-50 px-1.5 py-0.5 text-[10px] font-normal text-violet-700">
                  内部代号 trinity_cards
                </span>
              </h3>
            </div>
            <p className="text-[13px] leading-6">
              <b>固定 3 张图 → 1 段视频。</b>系统会把你的分镜提示词拆成角色卡、故事卡、视频卡，
              再把这 3 张图一起喂给视频模型，兼顾人物一致性、剧情瞬间和整体质感。
            </p>
            <ul className="mt-2 space-y-1 text-[12px] leading-5 text-slate-500">
              <li>
                <b className="text-slate-700">适合：</b>既想保持人物稳定，又想让 AI 帮你整理参考图语义的镜头
              </li>
              <li>
                <b className="text-emerald-700">优势：</b>比单图信息更完整；比手动多帧更省操作；适合角色一致性要求高的短剧镜头
              </li>
              <li>
                <b className="text-rose-700">劣势：</b>固定需要 3 张参考图；依赖三合一卡 AI 融合；当前更偏风格化工作流
              </li>
            </ul>
          </section>

          {/* 一句话总结 */}
          <section className="rounded-2xl border border-slate-200 bg-white p-4">
            <h4 className="mb-2 text-[11px] font-medium uppercase tracking-[0.18em] text-slate-500">
              一句话总结
            </h4>
            <ul className="space-y-2 text-[13px] leading-6">
              <li>
                <b className="text-sky-700">单图 → 视频</b>：你给模型 1 张图说"从这里开始拍"，剩下交给 AI。
              </li>
              <li>
                <b className="text-amber-700">多图 → 视频</b>：你给模型一组图说"按这个顺序过渡"，AI 负责把帧之间的过渡补完。
              </li>
              <li>
                <b className="text-violet-700">三合一参考</b>：你给模型 3 张分工明确的参考卡，让 AI 同时参考角色、剧情和画面质感。
              </li>
            </ul>
          </section>

          {/* 切换无损说明 */}
          <section className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4">
            <h4 className="mb-2 text-[11px] font-medium uppercase tracking-[0.18em] text-emerald-700">
              来回切换是安全的
            </h4>
            <p className="text-[12px] leading-5 text-emerald-900">
              三种模式都支持无损切换：关键帧候选、帧序列、三合一卡都会按各自方式保留；切回去时原来的素材仍在，
              不需要整套重做。
            </p>
          </section>
        </div>

        <div className="flex justify-end gap-2 border-t border-slate-100 bg-white px-5 py-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded-2xl bg-slate-950 px-4 py-2 text-sm font-semibold text-white transition hover:bg-slate-800"
          >
            知道了
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

function ModeOption({
  active,
  disabled,
  accent,
  icon,
  label,
  hint,
  onClick,
}: {
  active: boolean;
  disabled?: boolean;
  accent: 'sky' | 'amber' | 'violet';
  icon: React.ReactNode;
  label: string;
  hint?: string;
  onClick: () => void;
}) {
  const activeBg =
    accent === 'amber'
      ? 'bg-white text-amber-700 shadow-sm ring-1 ring-amber-200'
      : accent === 'violet'
        ? 'bg-white text-violet-700 shadow-sm ring-1 ring-violet-200'
      : 'bg-white text-sky-700 shadow-sm ring-1 ring-sky-200';
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || active}
      className={`relative flex flex-1 items-center justify-center gap-1.5 rounded-xl px-2 py-1.5 text-xs font-semibold transition ${
        active
          ? activeBg
          : 'text-slate-500 hover:text-slate-700 disabled:cursor-not-allowed disabled:opacity-40'
      }`}
      title={
        disabled && !active
          ? '视频已激活，请先取消或重激活后再切换'
          : hint || label
      }
      aria-label={hint ? `${label}：${hint}` : label}
    >
      {icon}
      {label}
    </button>
  );
}

function TabButton({
  active,
  onClick,
  icon,
  label,
  badge,
  highlight,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
  badge?: number;
  /** 不在激活态时仍带轻微状态色提示 */
  highlight?: 'sky' | 'emerald' | 'amber' | 'rose' | 'violet';
}) {
  const highlightDot =
    highlight === 'emerald'
      ? 'bg-emerald-400'
      : highlight === 'amber'
        ? 'bg-amber-400 animate-pulse'
        : highlight === 'rose'
          ? 'bg-rose-400'
          : highlight === 'violet'
            ? 'bg-violet-400'
          : highlight === 'sky'
            ? 'bg-sky-400'
            : null;

  return (
    <button
      type="button"
      onClick={onClick}
      className={`relative flex flex-1 items-center justify-center gap-1.5 whitespace-nowrap rounded-xl px-2 py-1.5 text-xs font-semibold transition ${
        active
          ? 'bg-white text-slate-900 shadow-sm'
          : 'text-slate-500 hover:text-slate-700'
      }`}
    >
      {icon}
      {label}
      {badge !== undefined && (
        <span
          className={`rounded-full px-1.5 text-[10px] font-bold ${
            active ? 'bg-sky-100 text-sky-700' : 'bg-slate-200 text-slate-600'
          }`}
        >
          {badge}
        </span>
      )}
      {highlightDot && !active && (
        <span className={`absolute right-1.5 top-1.5 h-1.5 w-1.5 rounded-full ${highlightDot}`} />
      )}
    </button>
  );
}

function AutosaveIndicator({ state }: { state: 'idle' | 'saving' | 'saved' | 'error' }) {
  if (state === 'idle') {
    return <span className="text-[11px] text-slate-400">自动保存</span>;
  }
  if (state === 'saving') {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] text-slate-500">
        <Loader2 className="h-3 w-3 animate-spin" /> 保存中
      </span>
    );
  }
  if (state === 'saved') {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] text-emerald-600">
        <Check className="h-3 w-3" /> 已保存
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-[11px] text-rose-600">
      <AlertCircle className="h-3 w-3" /> 保存失败
    </span>
  );
}

function ActivationStatusBar(_unused: {
  hasLockedKeyframe: boolean;
  activationStatus: ActivationStatus;
  onRequestActivate: () => void;
  onRequestReactivate: () => void;
  onRequestDeactivate: () => void;
  onGoToKeyframeTab: () => void;
}) {
  // 已不再使用：底部 sticky 状态条已移除，激活按钮统一回到「视频」tab
  return null;
}
void ActivationStatusBar;
